import { useEffect, useState } from 'react';
import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { OwnerOnly } from '@/lib/auth/OwnerOnly';
import { EdgeFunctionError } from '@/lib/api/edgeFunction';
import {
  MfaStepUpError,
  usePayRail,
  useRailStepUp,
  useSendRailOtp,
  useVerifyRailOtp,
} from '@/lib/data/settlement';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/settlement/verify')({
  component: SettlementVerifyPage,
  validateSearch: (search: Record<string, unknown>) => ({
    railId: typeof search.railId === 'string' ? search.railId : undefined,
  }),
});

function SettlementVerifyPage() {
  return (
    <OwnerOnly>
      <SettlementVerifyContent />
    </OwnerOnly>
  );
}

function CountdownTo({ iso }: { iso: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remainingMs = new Date(iso).getTime() - now;
  if (remainingMs <= 0) return <span>any moment now</span>;
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return (
    <span>
      {hours}h {minutes}m {seconds}s
    </span>
  );
}

function SettlementVerifyContent() {
  const { supabase } = useAuth();
  const { railId } = useSearch({ from: '/settlement/verify' });

  const railQuery = usePayRail(supabase, railId ?? null);
  const sendOtp = useSendRailOtp(supabase);
  const verifyOtp = useVerifyRailOtp(supabase, railId ?? null);
  const stepUp = useRailStepUp(supabase, railId ?? null);

  const [code, setCode] = useState('');
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stepUpCode, setStepUpCode] = useState('');
  const [stepUpError, setStepUpError] = useState<string | null>(null);

  if (!railId) {
    return (
      <section className="rounded-card border border-border bg-surface p-xl shadow-card">
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Verify pay rail</h1>
        <p className="mt-sm text-body text-ink-2">
          Open this page from a pay rail in{' '}
          <Link to="/settlement" className="text-canyon underline">
            Settlement settings
          </Link>
          .
        </p>
      </section>
    );
  }

  const rail = railQuery.data;

  return (
    <section className="mx-auto flex max-w-lg flex-col gap-lg">
      <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Verify pay rail</h1>

      {railQuery.isLoading ? <PageLoading /> : null}
      {railQuery.isError ? <p className="text-body text-danger">Could not load this pay rail.</p> : null}

      {rail ? (
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <p className="text-body text-ink-2">
            Status: <strong className="text-ink">{rail.status.replace('_', ' ')}</strong>
          </p>

          {rail.status === 'cooling_off' && rail.activates_at ? (
            <p className="mt-sm text-body text-ink">
              Live in <CountdownTo iso={rail.activates_at} />. Guests aren't sent to this rail yet.
            </p>
          ) : null}
          {rail.status === 'active' ? <p className="mt-sm text-body text-success">This pay rail is live.</p> : null}
          {rail.status === 'frozen' ? <p className="mt-sm text-body text-danger">This pay rail is frozen.</p> : null}

          {rail.status === 'pending_verification' ? (
            <div className="mt-lg flex flex-col gap-md">
              <div>
                <h2 className="text-h3 font-bold text-ink">Step 1 — email code</h2>
                {!rail.verified_email_at ? (
                  <div className="mt-sm flex flex-col gap-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        sendOtp.mutate(rail.id, {
                          onSuccess: (res) => setSendResult(`Code sent to ${res.sentTo}. It expires in 10 minutes.`),
                          onError: (err) => setError(err instanceof Error ? err.message : 'Could not send the code.'),
                        });
                      }}
                      disabled={sendOtp.isPending}
                      className="self-start rounded-button border border-border bg-surface px-md py-xs text-body font-semibold text-ink disabled:opacity-50"
                    >
                      {sendOtp.isPending ? 'Sending…' : 'Send email code'}
                    </button>
                    {sendResult ? <p className="text-body text-ink-2">{sendResult}</p> : null}
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        setError(null);
                        verifyOtp.mutate(code, {
                          onSuccess: () => setCode(''),
                          onError: (err) => {
                            if (err instanceof EdgeFunctionError) {
                              setError(err.message === 'wrong_code' ? 'That code is not right.' : err.message);
                              return;
                            }
                            setError('Could not verify that code.');
                          },
                        });
                      }}
                      className="flex items-end gap-sm"
                    >
                      <label className="flex flex-col gap-xs text-caption text-ink-2">
                        6-digit code
                        <input
                          value={code}
                          onChange={(e) => setCode(e.target.value)}
                          inputMode="numeric"
                          maxLength={6}
                          className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={verifyOtp.isPending || code.length !== 6}
                        className="rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
                      >
                        Verify
                      </button>
                    </form>
                    {error ? <p className="text-body text-danger">{error}</p> : null}
                  </div>
                ) : (
                  <p className="mt-sm text-body text-success">Confirmed.</p>
                )}
              </div>

              <div>
                <h2 className="text-h3 font-bold text-ink">Step 2 — authenticator app</h2>
                <p className="mt-xs text-body text-ink-2">
                  Enter a fresh code from your authenticator app. This re-checks your identity — it doesn't reuse the
                  code from signing in.
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setStepUpError(null);
                    stepUp.mutate(stepUpCode, {
                      onSuccess: () => setStepUpCode(''),
                      onError: (err) => {
                        if (err instanceof MfaStepUpError) {
                          if (err.step === 'verify') {
                            setStepUpError('That code is not right — try the newest code your app shows.');
                          } else if (err.step === 'challenge') {
                            setStepUpError('That challenge expired — try again with a fresh code.');
                          } else {
                            setStepUpError(err.message);
                          }
                          return;
                        }
                        if (err instanceof EdgeFunctionError && err.code === 'step_up_required') {
                          setStepUpError('Enter a fresh authenticator code and try again.');
                          return;
                        }
                        setStepUpError(err instanceof Error ? err.message : 'Could not confirm the step-up.');
                      },
                    });
                  }}
                  className="mt-sm flex items-end gap-sm"
                >
                  <label className="flex flex-col gap-xs text-caption text-ink-2">
                    6-digit authenticator code
                    <input
                      value={stepUpCode}
                      onChange={(e) => setStepUpCode(e.target.value)}
                      disabled={!rail.verified_email_at}
                      inputMode="numeric"
                      maxLength={6}
                      className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink disabled:opacity-50"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={!rail.verified_email_at || stepUp.isPending || stepUpCode.length !== 6}
                    className="rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
                  >
                    {stepUp.isPending ? 'Confirming…' : 'Confirm with authenticator'}
                  </button>
                </form>
                {stepUpError ? <p className="mt-sm text-body text-danger">{stepUpError}</p> : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <Link to="/settlement" className="text-body text-canyon underline">
        Back to settlement settings
      </Link>
    </section>
  );
}
